import { Gift, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
    getGiftCardOnlineAvailability,
    guestProbeIds,
    placeGiftCardHold,
    readGiftCardPublicError,
    releaseGiftCardHold,
    resizeGiftCardHold,
    TurnstileWidget,
    type GiftCardPinMode,
} from '@order-up/client/vite';

/**
 * Pay part — or all — of an online order with a gift card (spec 2026-10-08
 * §4.2). Server-authoritative, like the promo code beside it:
 *
 *   - Applying a card places a HOLD for min(available, order total) on the
 *     server; the component shows the amount the SERVER held, never one it
 *     computed, and the parent charges the card only the remainder.
 *   - When the total moves (tip, promo), the hold is re-sized by its id — no
 *     card number is sent again.
 *   - Removing the card releases the hold. A hold that ended (30 min, a
 *     declined card) is cleared and the guest is asked to apply it again.
 *
 * Self-hides unless the restaurant offers gift cards online
 * (`GET /public/gift-cards/:slug/availability`, dark behind
 * initiative.gift_cards_online). The number is held only while the guest
 * types it; it is never stored. Where the restaurant asks for gift card PINs
 * (spec §13) the same availability read says whether the PIN field is
 * optional ("if your card has one") or required; the PIN is sent once, with
 * the number, and is never kept here either.
 */

export interface AppliedGiftCard {
    holdId: string;
    amountCents: number;
    last4: string;
}

export interface GiftCardTenderProps {
    slug: string;
    cartId: number | null;
    /** The order total the server previewed, in cents (null while pending). */
    totalCents: number | null;
    applied: AppliedGiftCard | null;
    onChange: (applied: AppliedGiftCard | null) => void;
    /** A message from the parent (e.g. the hold ended at submit). */
    notice?: string | null;
}

type Phase =
    | { kind: 'idle' }
    | { kind: 'applying' }
    | { kind: 'challenge'; siteKey: string; attempt: number }
    | { kind: 'error'; message: string };

function dollars(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
}

export default function GiftCardTender({ slug, cartId, totalCents, applied, onChange, notice }: GiftCardTenderProps) {
    const [available, setAvailable] = useState(false);
    const [open, setOpen] = useState(false);
    const [number, setNumber] = useState('');
    const [pin, setPin] = useState('');
    const [pinMode, setPinMode] = useState<GiftCardPinMode>('off');
    const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
    const lastSizedFor = useRef<number | null>(null);
    // The parent passes a fresh callback each render; reading it through a ref
    // keeps the re-size effect from re-running (and dropping its own in-flight
    // answer) on every parent render.
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        let cancelled = false;
        void getGiftCardOnlineAvailability(slug).then((a) => {
            if (cancelled) return;
            setAvailable(a.available);
            setPinMode(a.pin);
        });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    // Re-size the hold when the order total moves.
    useEffect(() => {
        if (!applied || cartId === null || totalCents === null || totalCents <= 0) return;
        if (lastSizedFor.current === totalCents) return;
        lastSizedFor.current = totalCents;
        let cancelled = false;
        resizeGiftCardHold(cartId, applied.holdId, totalCents)
            .then((r) => {
                if (cancelled) return;
                if (r.status === 'held' && r.holdId) {
                    if (r.amountCents !== applied.amountCents) onChangeRef.current({ holdId: r.holdId, amountCents: r.amountCents, last4: r.last4 });
                } else {
                    onChangeRef.current(null);
                    setPhase({ kind: 'error', message: `No balance is available on card ending ${r.last4}.` });
                }
            })
            .catch((err) => {
                if (cancelled) return;
                onChangeRef.current(null);
                setPhase({ kind: 'error', message: readGiftCardPublicError(err).message });
            });
        return () => {
            cancelled = true;
        };
    }, [applied, cartId, totalCents]);

    if (!available || cartId === null) return null;

    const apply = async (turnstileToken?: string) => {
        if (totalCents === null || totalCents <= 0) return;
        setPhase({ kind: 'applying' });
        try {
            const r = await placeGiftCardHold(cartId, totalCents, {
                number,
                ...(pinMode !== 'off' && pin ? { pin } : {}),
                ...guestProbeIds(),
                ...(turnstileToken ? { turnstileToken } : {}),
            });
            if (r.status === 'held' && r.holdId) {
                lastSizedFor.current = totalCents;
                onChange({ holdId: r.holdId, amountCents: r.amountCents, last4: r.last4 });
                setNumber('');
                setPin('');
                setPhase({ kind: 'idle' });
            } else {
                setPhase({
                    kind: 'error',
                    message: `No balance is available on card ending ${r.last4}.${pinMode !== 'off' ? ' Check the card number and PIN and try again.' : ''} If you think that is wrong, please contact the restaurant.`,
                });
            }
        } catch (err) {
            const e = readGiftCardPublicError(err);
            if (e.code === 'TURNSTILE_REQUIRED' && e.siteKey) {
                setPhase((prev) => ({
                    kind: 'challenge',
                    siteKey: e.siteKey as string,
                    attempt: prev.kind === 'challenge' ? prev.attempt + 1 : 0,
                }));
                return;
            }
            setPhase({ kind: 'error', message: e.message });
        }
    };

    const remove = async () => {
        if (!applied || cartId === null) return;
        const holdId = applied.holdId;
        onChange(null);
        lastSizedFor.current = null;
        try {
            await releaseGiftCardHold(cartId, holdId);
        } catch {
            // The hold expires on its own; nothing for the guest to do.
        }
    };

    if (applied) {
        const remainder = totalCents === null ? null : Math.max(0, totalCents - applied.amountCents);
        return (
            <div className="rounded-2xl border border-green-200 bg-green-50 p-4 shadow-sm" data-testid="gift-card-applied">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Gift className="h-5 w-5 shrink-0 text-green-700" aria-hidden />
                        <div>
                            <p className="font-semibold text-gray-900">
                                Gift card ending {applied.last4}{' '}
                                <span className="text-green-700">− {dollars(applied.amountCents)}</span>
                            </p>
                            {remainder !== null ? (
                                <p className="text-xs text-gray-600" data-testid="gift-card-remainder">
                                    {remainder === 0
                                        ? 'Your gift card covers the whole order.'
                                        : `${dollars(remainder)} left to pay by card.`}
                                </p>
                            ) : null}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => void remove()}
                        aria-label="Remove gift card"
                        className="flex shrink-0 items-center gap-1 text-xs font-medium text-gray-600 underline hover:text-gray-900"
                    >
                        <X className="h-3.5 w-3.5" aria-hidden /> Remove
                    </button>
                </div>
            </div>
        );
    }

    const isOpen = open || phase.kind !== 'idle' || Boolean(notice);
    return (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={isOpen}
                className="flex w-full items-center justify-between gap-3 p-4 text-left transition-colors hover:bg-gray-50"
            >
                <span className="flex items-center gap-2">
                    <Gift className="h-5 w-5 text-gray-700" aria-hidden />
                    <span className="font-semibold text-gray-900">Pay with a gift card</span>
                </span>
                <Plus
                    className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-45' : ''}`}
                    aria-hidden
                />
            </button>
            {isOpen && (
                <div className="space-y-2 px-4 pb-4">
                    {notice ? <p className="text-xs font-medium text-amber-700">{notice}</p> : null}
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            spellCheck={false}
                            value={number}
                            onChange={(e) => {
                                setNumber(e.target.value.replace(/[^\d\s-]/g, '').slice(0, 24));
                                if (phase.kind === 'error') setPhase({ kind: 'idle' });
                            }}
                            placeholder="Gift card number"
                            aria-label="Gift card number"
                            className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm tracking-wider text-gray-900 focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                        />
                        <button
                            type="button"
                            onClick={() => void apply()}
                            disabled={phase.kind === 'applying' || phase.kind === 'challenge' || number.replace(/\D/g, '').length < 8 || (pinMode === 'all' && pin.length < 4) || totalCents === null}
                            className="shrink-0 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            {phase.kind === 'applying' ? 'Applying…' : 'Apply'}
                        </button>
                    </div>
                    {pinMode !== 'off' ? (
                        <input
                            type="password"
                            inputMode="numeric"
                            autoComplete="off"
                            value={pin}
                            onChange={(e) => {
                                setPin(e.target.value.replace(/\D/g, '').slice(0, 8));
                                if (phase.kind === 'error') setPhase({ kind: 'idle' });
                            }}
                            placeholder={pinMode === 'all' ? 'PIN' : 'PIN, if your card has one'}
                            aria-label="Gift card PIN"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm tracking-widest text-gray-900 focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900/10"
                        />
                    ) : null}
                    {phase.kind === 'challenge' ? (
                        <div className="space-y-1">
                            <p className="text-xs text-gray-700">Please confirm you are not a robot.</p>
                            <TurnstileWidget
                                key={phase.attempt}
                                siteKey={phase.siteKey}
                                onToken={(token) => void apply(token)}
                                onError={() => setPhase({ kind: 'error', message: 'We could not verify this browser. Please reload and try again.' })}
                            />
                        </div>
                    ) : null}
                    {phase.kind === 'error' ? (
                        <p className="text-xs font-medium text-red-600" role="alert">
                            {phase.message}
                        </p>
                    ) : null}
                </div>
            )}
        </div>
    );
}
